import React, { useEffect, useState, useRef } from "react";
import {
  X,
  Plus,
  Trash2,
  ArrowLeft,
  Calendar,
  User,
  Clock,
  Package,
  MessageSquare,
  Truck,
  ExternalLink,
} from "lucide-react";
import { useAlert } from "components/UI_components/Alert";
import { BASE_URL } from "utils/constant";
import DeliveryPaymentFlow from "./DelivaryPaymentFlow";
import {
  NewCommentModal,
  deleteComment,
} from "./DeliveryModals";

const DeliveryDetails = ({
  Delivery,
  onClose,
  setTab,
  setDelivery,
  userPermissions,
  onMarkReceived,
  onMarkReturned,
}) => {
  const [comments, setComments] = useState([]);
  const [delivery, setDeliveryState] = useState(Delivery);
  const [materialLibraryMap, setMaterialLibraryMap] = useState({});
  const [materialLibraryByName, setMaterialLibraryByName] = useState({});
  const [showPaymentFlow, setShowPaymentFlow] = useState(false);
  const [showNewComment, setShowNewComment] = useState(false);
  const menuRef = useRef(null);
  const hasEditPermission = userPermissions?.edit_material_management ||
    userPermissions?.edit_all ||
    userPermissions?.is_admin;

  const formatDate = (date) => {
    const d = new Date(date);

    // Format time as "hh:mmam/pm"
    let time = d
      .toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();

    // Remove the space between time and am/pm
    time = time.replace(" ", "");

    // Format date as "dd Month, yyyy"
    const day = d.getDate().toString().padStart(2, "0");
    const month = d.toLocaleString("default", { month: "long" });
    const year = d.getFullYear();

    return `${time}, ${day} ${month}, ${year}`;
  };

  const formatMoney = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const n = Number(value);
    if (Number.isNaN(n)) return `₹ ${String(value)}`;
    return `₹ ${n.toLocaleString("en-IN")}`;
  };

  const safeShortDate = (date) => {
    if (!date) return "—";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString();
  };

  const statusText =
    delivery?.transaction_id === 1
      ? "Pending"
      : delivery?.transaction_id === 3
        ? "Returned"
        : "Received";

  const [menuOpen, setMenuOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const pickFirstText = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      const text = String(value).trim();
      if (text) return text;
    }
    return "N/A";
  };

  const getMaterialMeta = (material) => {
    if (!material) {
      return { specification: "N/A", subcategory: "N/A", brand: "N/A" };
    }

    const materialObj =
      typeof material?.material === "object" && material?.material !== null
        ? material.material
        : null;
    const materialId =
      material?.material_id ||
      material?.material ||
      materialObj?.id ||
      null;
    const libMaterialById = materialId ? materialLibraryMap[String(materialId)] : null;
    const libMaterialByName =
      materialLibraryByName[
      String(material?.material_name || materialObj?.material_name || "")
        .trim()
        .toLowerCase()
      ] || null;
    const libMaterial = libMaterialById || libMaterialByName;

    const specification = pickFirstText(
      material?.specification,
      material?.material_specification,
      material?.material_spec,
      material?.spec,
      materialObj?.specification,
      materialObj?.material_specification,
      materialObj?.material_spec,
      libMaterial?.specification,
      libMaterial?.material_specification,
      libMaterial?.material_spec,
    );

    const subcategory = pickFirstText(
      material?.material_subcategory,
      material?.subcategory,
      material?.category,
      materialObj?.subcategory,
      materialObj?.category,
      libMaterial?.subcategory,
      libMaterial?.category,
    );

    const brand = pickFirstText(
      material?.brand_name,
      material?.material_brand_name,
      material?.brand,
      materialObj?.brand_name,
      materialObj?.material_brand_name,
      materialObj?.brand,
      libMaterial?.brand_name,
      libMaterial?.material_brand_name,
      libMaterial?.brand,
    );

    return { specification, subcategory, brand };
  };

  const handleReceiveClick = () => {
    setShowPaymentFlow(true);
  };

  const handleSaveClick = () => {
    setIsEditMode(false);
    onClose(); // close the drawer
  };
  const handleDeleteDelivery = () => {
    addAlert(
      "This will permanently delete the delivery and associated inventory updates.",
      "confirm",
      {
        onConfirm: async () => {
          try {
            const accessToken = localStorage.getItem("access");
            const response = await fetch(
              `${BASE_URL}/inventory/delete-delivery/${delivery?.id}/`,
              {
                method: "DELETE",
                headers: { Authorization: `Bearer ${accessToken}` },
              },
            );
            const data = await response.json();
            if (data.status) {
              onClose();
            } else {
              addAlert(data.message || "Failed to delete delivery", "error");
            }
          } catch (error) {
            console.error("Error deleting delivery:", error);
            addAlert("An error occurred while deleting the delivery", "error");
          }
        },
      },
    );
  };

  const handleDeleteComment = async (commentId) => {
    const success = await deleteComment(commentId);
    if (success) {
      fetchComments(); // Refresh comments after deletion
    }
  };

  const handlePaymentFlowClose = () => {
    setShowPaymentFlow(false);
    // onClose();
  };

  const handleMarkedReceived = () => {
    setShowPaymentFlow(false);
    if (onMarkReceived) {
      onMarkReceived();
    } else {
      onClose();
    }
  };

  const handleMarkedReturned = () => {
    setShowPaymentFlow(false);
    if (onMarkReturned) {
      onMarkReturned();
    } else {
      onClose();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [menuOpen]);

  const getDelivery = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/inventory/get-a-delivery/${Delivery?.id}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const data = await response.json();
      if (data?.status) {
        setDeliveryState(data?.data);
      }
    } catch (error) {
      console.error("Error fetching delivery:", error);
    }
  };

  const fetchComments = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/inventory/get-all-comments-of-delivery/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ delivery: delivery?.id }),
        },
      );
      const data = await response.json();
      if (data.status) {
        setComments(data.data);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const getMaterialLibrary = async () => {
    try {
      const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
      if (!orgId) return;

      const response = await fetch(`${BASE_URL}/org/material-library/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ org_id: orgId }),
      });

      const data = await response.json();
      const list = Array.isArray(data) ? data : [];

      const byId = {};
      const byName = {};
      list.forEach((item) => {
        if (item?.id !== null && item?.id !== undefined) {
          byId[String(item.id)] = item;
        }
        const nameKey = String(item?.material_name || "").trim().toLowerCase();
        if (nameKey) byName[nameKey] = item;
      });

      setMaterialLibraryMap(byId);
      setMaterialLibraryByName(byName);
    } catch (error) {
      console.error("Error fetching material library:", error);
    }
  };

  const { addAlert } = useAlert();
  useEffect(() => {
    if (Delivery?.id) {
      fetchComments();
      getDelivery();
      getMaterialLibrary();
    }
  }, [Delivery?.id]);

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full md:w-2/5 h-full bg-white border-l border-gray-200 shadow-xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
      {showPaymentFlow ? (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <DeliveryPaymentFlow
            onClose={handlePaymentFlowClose}
            delivery={delivery}
            onMarkedReceived={handleMarkedReceived}
            onMarkedReturned={handleMarkedReturned}
          />
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="flex justify-between items-center px-4 py-3 bg-white border-b border-gray-200 sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <button
                className="w-8 h-8 rounded-full hover:bg-gray-200 flex items-center justify-center text-[#232323]"
                onClick={onClose}
              >
                <ArrowLeft size={18} />
              </button>
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Delivery Details
                </h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {delivery?.po_number
                      ? `#${delivery?.po_number}`
                      : `#DLRV-${delivery?.id}`}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-widest ${delivery?.transaction_id === 3
                      ? "bg-red-100 text-red-600"
                      : delivery?.transaction_id === 1
                        ? "bg-sm-accent/10 text-sm-accent"
                        : "bg-emerald-100 text-emerald-700"
                      }`}
                  >
                    {delivery?.transaction_id === 3
                      ? "Cancelled"
                      : delivery?.transaction_id === 1
                        ? "Pending"
                        : "Received"}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {hasEditPermission && (
                <button
                  className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors text-slate-500 flex items-center gap-2"
                  onClick={() => setIsEditMode(!isEditMode)}
                  title={isEditMode ? "Exit Edit Mode" : "Enter Edit Mode"}
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider">{isEditMode ? "Done" : "Edit"}</span>
                </button>
              )}
              {/* <div className="h-4 w-px bg-slate-200 mx-1" />
              <button
                className="p-1.5 hover:bg-red-50 rounded-lg transition-colors text-red-500"
                onClick={handleDeleteDelivery}
                title="Delete Delivery"
              >
                <Trash2 size={18} />
              </button> */}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar px-4 py-4">
            <div className="space-y-4">
              {/* Top Summary & Timeline */}
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-white px-4 py-4 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                    <div className="flex gap-4 min-w-0">
                      <div className="h-11 w-11 bg-sm-primary/5 rounded-xl flex items-center justify-center text-sm-primary shrink-0">
                        <Truck size={22} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-base font-semibold text-slate-900 truncate">
                            {delivery?.vendor_name || "—"}
                          </h3>
                          <span className="bg-slate-100 px-1.5 py-0.5 rounded text-[9px] uppercase font-semibold tracking-wider text-slate-600">
                            Vendor
                          </span>
                        </div>
                        <div className="mt-1 text-sm font-medium text-slate-600 flex flex-wrap items-center gap-x-4 gap-y-1">
                          <span className="inline-flex items-center gap-1.5">
                            <User size={14} className="text-slate-400" />
                            {delivery?.contact_no || "—"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-6 md:text-right">
                      <div>
                        <p className="text-[9px] uppercase font-semibold tracking-widest text-slate-400 mb-1">
                          Purchase Code
                        </p>
                        <p className="text-sm font-semibold text-slate-900">
                          {delivery?.po_number
                            ? `#${delivery?.po_number}`
                            : `#DLRV-${delivery?.id}`}
                        </p>
                      </div>
                      <div>
                        <p className="text-[9px] uppercase font-semibold tracking-widest text-slate-400 mb-1">
                          {delivery?.transaction_id === 3
                            ? "Cancelled Date"
                            : delivery?.transaction_id === 1
                              ? "Expected Date"
                              : "Success Date"}
                        </p>
                        <p className="text-sm font-semibold text-slate-900 flex items-center md:justify-end gap-1.5">
                          <Calendar size={13} className="text-sm-primary" />
                          {delivery?.transaction_id === 1
                            ? delivery?.date || "—"
                            : safeShortDate(delivery?.last_updated_on)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-3 border-t border-slate-100 grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-widest mb-1">
                        Status
                      </p>
                      <span className={`inline-flex px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${delivery?.transaction_id === 1
                        ? "bg-amber-100 text-amber-700"
                        : delivery?.transaction_id === 2
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-red-100 text-red-700"
                        }`}>
                        {statusText}
                      </span>
                    </div>
                    <div>
                      <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-widest mb-1">
                        Items
                      </p>
                      <p className="text-sm font-bold text-slate-900">
                        {delivery?.all_materials?.length || 0} Products
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-widest mb-1">
                        Receipt Image
                      </p>
                      {delivery?.receipt_image ? (
                        <button
                          onClick={() => window.open(delivery.receipt_image, "_blank")}
                          className="text-xs font-semibold text-sm-primary hover:underline flex items-center gap-1"
                        >
                          View Receipt <ExternalLink size={12} />
                        </button>
                      ) : (
                        <p className="text-xs font-medium text-slate-400">Not Available</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Timeline info Card */}
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-col justify-center">
                  <div className="space-y-4">
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                        <User size={10} className="text-sm-primary" />
                        Created By
                      </p>
                      <p className="text-xs font-semibold text-slate-800">
                        {delivery?.created_by || "System Admin"}
                      </p>
                      <span className="text-[10px] font-medium text-slate-500 block mt-0.5">
                        {delivery?.created_on ? formatDate(delivery.created_on) : "N/A"}
                      </span>
                    </div>
                    <div className="pt-4 border-t border-slate-200/60">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                        <Clock size={10} className="text-sm-primary" />
                        Last Updated
                      </p>
                      <p className="text-xs font-semibold text-slate-800">
                        {delivery?.last_updated_by || delivery?.created_by || "System Admin"}
                      </p>
                      <span className="text-[10px] font-medium text-slate-500 block mt-0.5">
                        {delivery?.last_updated_on ? formatDate(delivery.last_updated_on) : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content: Materials & Comments */}
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
                  <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-2.5">
                      <Package size={16} className="text-sm-primary" />
                      <h3 className="font-semibold text-slate-800">Content</h3>
                    </div>
                    <span className="bg-sm-primary/10 text-sm-primary text-[9px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider">
                      {delivery?.all_materials?.length || 0} Items
                    </span>
                  </div>

                  <div className="divide-y divide-slate-100 overflow-y-auto max-h-[500px] custom-scrollbar">
                    {delivery?.all_materials?.map((material, index) => {
                      const meta = getMaterialMeta(material);
                      return (
                        <div
                          key={material.id}
                          className="px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3.5">
                              <span className="text-[10px] font-semibold text-slate-300">
                                {String(index + 1).padStart(2, "0")}
                              </span>
                              <div>
                                <h4 className="font-medium text-slate-800">{material.material_name}</h4>
                              </div>
                            </div>
                            <div className="bg-white border border-slate-200 rounded-lg px-2.5 py-1 shadow-sm">
                              <span className="text-xs font-semibold text-slate-900">{material.quantity}</span>
                              <span className="text-[9px] font-semibold text-slate-500 ml-1 uppercase">{material.unit}</span>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-3 mt-3 pl-7">
                            <div>
                              <p className="text-xs text-gray-500">Specification</p>
                              <p className="text-sm font-medium text-gray-900">{meta.specification}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Subcategory</p>
                              <p className="text-sm font-medium text-gray-900">{meta.subcategory}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Brand</p>
                              <p className="text-sm font-medium text-gray-900">{meta.brand}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
                  <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-2.5">
                      <MessageSquare size={16} className="text-sm-primary" />
                      <h3 className="font-semibold text-slate-800">Comments</h3>
                    </div>
                    {isEditMode && (
                      <button
                        onClick={() => setShowNewComment(true)}
                        className="p-1 bg-sm-primary/10 text-sm-primary rounded-lg hover:bg-sm-primary/20 transition-colors"
                      >
                        <Plus size={14} />
                      </button>
                    )}
                  </div>
                  <div className="p-4 overflow-y-auto max-h-[500px] custom-scrollbar">
                    {comments.length === 0 ? (
                      <div className="py-12 text-center bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                        <p className="text-slate-400 text-xs font-medium">No comments available</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {comments.map((comment) => (
                          <div key={comment.id} className="p-3 bg-slate-50 rounded-xl relative group border border-slate-100">
                            <div className="flex justify-between items-start mb-1.5">
                              <span className="text-[9px] font-semibold text-slate-400 flex items-center gap-1">
                                <Clock size={10} /> {formatDate(comment.created_on)}
                              </span>
                              {isEditMode && (
                                <button
                                  className="text-slate-300 hover:text-red-500 transition-colors"
                                  onClick={() => handleDeleteComment(comment.id)}
                                >
                                  <X size={14} />
                                </button>
                              )}
                            </div>
                            <p className="text-xs text-slate-700 font-medium leading-relaxed">{comment.comment}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Fixed Footer Buttons */}
          {delivery?.transaction_id === 1 && delivery?.transaction_id !== 3 && hasEditPermission && (
            <div className="px-5 py-3 bg-white border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.03)] flex gap-3 justify-end">
              <button
                onClick={handleSaveClick}
                className={`px-6 py-2.5 rounded-xl font-semibold text-xs transition-all shadow-sm ${isEditMode
                  ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  : "bg-slate-50 text-slate-300 cursor-not-allowed"
                  }`}
                disabled={!isEditMode}
              >
                Close
              </button>
              <button
                onClick={handleReceiveClick}
                className="px-6 py-2.5 bg-sm-primary text-white rounded-xl font-semibold text-xs shadow-lg shadow-sm-primary/20 hover:brightness-110 active:scale-95 transition-all flex items-center gap-1.5"
              >
                <span>Receive or Return</span>
                <Plus size={16} />
              </button>
            </div>
          )}

          {/* New Comment Modal */}
          {showNewComment && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
              <NewCommentModal
                onClose={() => setShowNewComment(false)}
                delivery={delivery}
                onSuccess={() => {
                  fetchComments();
                  getDelivery();
                }}
              />
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
};

export default DeliveryDetails;
